import { getCategoryName } from '@/utils/assessment-analysis'
import { useNavigate } from 'react-router'

interface AssessmentData {
  id: string
  status: string
  completedAt?: string
  scores?: Record<string, number>
  riskLevels?: Record<string, string>
  attemptNumber?: number
}

interface ResultsContentProps {
  selectedResult: string
  assessmentsData: Record<string, AssessmentData>
}

export default function ResultsContent({ selectedResult, assessmentsData }: ResultsContentProps) {
  const navigate = useNavigate()

  // Helper function to convert API risk levels to internal category
  const getRiskCategory = (riskLevel: string | undefined): 'low' | 'average' | 'high' => {
    if (!riskLevel) return 'low'
    const normalized = riskLevel.toLowerCase()
    if (normalized === 'low' || normalized === 'no risk') return 'low'
    if (normalized === 'moderate') return 'average'
    if (normalized === 'high' || normalized === 'serious') return 'high'
    return 'low'
  }

  // Get recommendation message based on risk level and category name
  const getRecommendationMessage = (riskCategory: 'low' | 'average' | 'high', categoryName: string) => {
    switch (riskCategory) {
      case 'low':
        return {
          title: 'Looking good!',
          message: `You scored low in this category: this indicates that there is a low tendency for violence as it relates to ${categoryName}. You would be moving to the next phase! Congratulations.`,
          showLink: false,
        }
      case 'average':
        return {
          title: 'Fair enough...',
          message: `Your scores came out as average in this category; however, we are leaving nothing to chance — we would be referring you to a correctional video to add colour to any grey areas!`,
          showLink: true,
          linkText: 'Kindly click the link below to get access to the correctional modules.',
        }
      case 'high':
        return {
          title: 'Ooops!',
          message: `You scored high in this category: there is tendency of violence and lots of room for improvement. This is the whole essence of putting together IRIS to rid your marriage off any form of violence. Based on your score, we would be directing you to a series of correctional videos.`,
          showLink: true,
          linkText: 'Kindly click here to access them.',
        }
    }
  }

  // Get result data from API
  const getResultData = () => {
    if (selectedResult === 'overall') {
      // For overall view, get the latest assessment or aggregate data
      const assessmentIds = Object.keys(assessmentsData)
      if (assessmentIds.length === 0) {
        return {
          title: 'Overall Performance',
          subtitle: 'No assessment results available',
          description: 'Complete your assessment to see your results here.',
          categories: [],
        }
      }

      // Get the most recent assessment
      const latestId = assessmentIds[0]
      if (!latestId) {
        return {
          title: 'Overall Performance',
          subtitle: 'No assessment data available',
          description: '',
          categories: [],
        }
      }
      const latestAssessment = assessmentsData[latestId]

      if (!latestAssessment || !latestAssessment.scores || !latestAssessment.riskLevels) {
        return {
          title: 'Overall Performance',
          subtitle: 'Assessment data incomplete',
          description: '',
          categories: [],
        }
      }

      const date = latestAssessment.completedAt
        ? new Date(latestAssessment.completedAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).replace(/\//g, '-')
        : ''

      const categories = Object.keys(latestAssessment.scores)
        .filter(key => key !== 'overall')
        .map((category, index) => {
          const riskLevel = latestAssessment.riskLevels![category] || 'low'
          const categoryName = getCategoryName(category)
          const riskCategory = getRiskCategory(riskLevel)
          const recommendation = getRecommendationMessage(riskCategory, categoryName)

          return {
            id: `${index + 1}`,
            title: categoryName,
            riskCategory,
            recommendation,
          }
        })

      return {
        title: 'Overall Performance',
        subtitle: date ? `Assessment Result: ${date}` : 'Assessment Result',
        description: 'Your results show areas where your relationship is strong, and areas where growth is possible. This is not a judgment but a guide to help you and your partner build a healthier, more loving marriage.',
        categories,
      }
    } else {
      // Get specific assessment data
      const assessment = assessmentsData[selectedResult]

      if (!assessment || !assessment.scores || !assessment.riskLevels) {
        return {
          title: 'Assessment Result',
          subtitle: 'Assessment data not found',
          description: '',
          categories: [],
        }
      }

      const date = assessment.completedAt
        ? new Date(assessment.completedAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).replace(/\//g, '-')
        : ''

      const categories = Object.keys(assessment.scores)
        .filter(key => key !== 'overall')
        .map((category, index) => {
          const riskLevel = assessment.riskLevels![category] || 'low'
          const categoryName = getCategoryName(category)
          const riskCategory = getRiskCategory(riskLevel)
          const recommendation = getRecommendationMessage(riskCategory, categoryName)

          return {
            id: `${index + 1}`,
            title: categoryName,
            riskCategory,
            recommendation,
          }
        })

      const attemptLabel = assessment.attemptNumber ? ` (Attempt #${assessment.attemptNumber})` : ''

      return {
        title: `Assessment Result${attemptLabel}`,
        subtitle: date,
        description: 'Your results show areas where your relationship is strong, and areas where growth is possible. This is not a judgment but a guide to help you and your partner build a healthier, more loving marriage.',
        categories,
      }
    }
  }

  const resultData = getResultData()

  const getRiskCategoryStyle = (riskCategory: 'low' | 'average' | 'high') => {
    switch (riskCategory) {
      case 'low':
        return { color: '#0ECF5B', iconColor: '#0ECF5B', bgColor: 'rgba(14, 207, 91, 0.1)' }
      case 'average':
        return { color: '#FFB319', iconColor: '#FFB319', bgColor: 'rgba(255, 179, 25, 0.1)' }
      case 'high':
        return { color: '#FF7043', iconColor: '#FF7043', bgColor: 'rgba(255, 112, 67, 0.1)' }
    }
  }

  return (
    <div className='flex flex-col gap-[4px]'>
      {/* Header */}
      <div className='flex flex-col gap-[4px] mb-6'>
        <h2 className='text-[20px] font-bold text-[#151515] leading-[32px]'>
          {resultData.title}
        </h2>
        {resultData.description && (
          <p className='text-[16px] font-normal text-[#151515] leading-[22px]'>
            {resultData.description}
          </p>
        )}
      </div>

      {/* Performance Categories */}
      <div className='flex flex-col gap-[19px]'>
        {resultData.categories.length === 0 ? (
          <div className='bg-[rgba(243,244,246,0.45)] rounded-[12px] p-[30px] text-center'>
            <p className='text-[16px] text-[#4e4d4d]'>No category data available.</p>
          </div>
        ) : (
          resultData.categories.map((category) => {
            const riskStyle = getRiskCategoryStyle(category.riskCategory)

            return (
              <div
                key={category.id}
                className='bg-[rgba(243,244,246,0.45)] rounded-[12px] p-[30px]'
              >
                {/* Category Title */}
                <h3 className='text-[18px] font-bold text-black leading-[28px] mb-4'>
                  {category.title}
                </h3>

                {/* Recommendation Title */}
                <div className='flex items-center gap-2 mb-3'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z'
                      fill={riskStyle.iconColor}
                    />
                  </svg>
                  <span className='text-[16px] font-bold leading-[24px]' style={{ color: riskStyle.color }}>
                    {category.recommendation.title}
                  </span>
                </div>

                {/* Recommendation Message */}
                <p className='text-[14px] font-normal text-[#4e4d4d] leading-[22px] text-justify mb-4'>
                  {category.recommendation.message}
                </p>

                {/* Link Text and Take Training Button - Only for average and high risk */}
                {category.recommendation.showLink && (
                  <div className='flex flex-col gap-3'>
                    <p className='text-[14px] font-medium text-[#4e4d4d] leading-[22px]'>
                      {category.recommendation.linkText}
                    </p>
                    <button
                      onClick={() => navigate('/learning-path')}
                      className='bg-[#FA6017] text-white rounded-[8px] px-[16px] py-[10px] text-[14px] font-semibold leading-[24px] hover:bg-[#e55109] transition-colors w-fit'
                    >
                      Access Correctional Modules
                    </button>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
